/**
 * IA Fillings Component
 * Fetches SEC filings JSON and renders rows into the table body.
 *
 * Source: https://data.sec.gov/submissions/CIK0001901297.json
 * View URL format: https://www.sec.gov/Archives/edgar/data/1901297/{accessionNoNoDashes}/{primaryDocument}
 */

class IAFillings extends HTMLElement {
  constructor() {
    super();

    this.filingsList = this.querySelector('[data-fillings-list]');
    this.yearSelect = this.querySelector('[data-fillings-filter-year]');
    this.formSelect = this.querySelector('[data-fillings-filter-form]');
    this.loadMoreBtn = this.querySelector('[data-fillings-load-more]');
    this.controlsWrap = this.querySelector('[data-fillings-controls]');

    this.allFilings = [];
    this.filteredFilings = [];
    this.itemsPerPage = 10;
    this.currentPage = 1;

    this.secSubmissionsUrl = 'https://data.sec.gov/submissions/CIK0001901297.json';
    this.issuerCikNumeric = '1901297';

    this.init();
  }

  init() {
    if (!this.filingsList) return;

    if (this.loadMoreBtn) {
      this.loadMoreBtn.addEventListener('click', () => this.loadMore());
    }

    this.fetchAndRenderFilings();
  }

  async fetchAndRenderFilings() {
    try {
      const response = await fetch(this.secSubmissionsUrl, {
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch SEC filings');
      }

      const data = await response.json();
      this.processFilingsData(data);
      this.populateFilters();
      this.applyFilters(true);
      this.bindFilterEvents();
    } catch (error) {
      // If the SEC endpoint is unavailable or blocked by CORS, fail silently in the UI
      console.error('[IA Fillings] Error fetching SEC filings', error);
      this.hideLoadMoreButton();
    }
  }

  processFilingsData(data) {
    const recent = data && data.filings && data.filings.recent;
    if (!recent) return;

    const filingDates = recent.filingDate || [];
    const forms = recent.form || [];
    const primaryDocuments = recent.primaryDocument || [];
    const accessionNumbers = recent.accessionNumber || [];

    const filings = [];

    for (let i = 0; i < filingDates.length; i++) {
      const formType = forms[i];
      if (!formType) continue;
      const date = filingDates[i];
      const primaryDocument = primaryDocuments[i];
      const accessionNumber = accessionNumbers[i];

      if (!date || !primaryDocument || !accessionNumber) continue;

      const viewUrl = this.buildViewUrl(accessionNumber, primaryDocument);
      const year = String(date).slice(0, 4);

      filings.push({
        date,
        year,
        formType,
        description: formType,
        viewUrl,
      });
    }

    // Sort by most recent date first
    filings.sort((a, b) => {
      return new Date(b.date) - new Date(a.date);
    });

    this.allFilings = filings;
  }

  buildViewUrl(accessionNumber, primaryDocument) {
    const accessionNoNoDashes = String(accessionNumber).replace(/-/g, '');
    return (
      'https://www.sec.gov/Archives/edgar/data/' +
      this.issuerCikNumeric +
      '/' +
      accessionNoNoDashes +
      '/' +
      primaryDocument
    );
  }

  clearRows() {
    this.filingsList.innerHTML = '';
  }

  renderRows(filings) {
    this.clearRows();

    filings.forEach((filing) => {
      const row = this.createRowElement(filing);
      this.filingsList.appendChild(row);
    });
  }

  createRowElement(filing) {
    const tr = document.createElement('tr');
    tr.className = 'ia-fillings__row';

    const dateTd = document.createElement('td');
    dateTd.className = 'ia-fillings__cell ia-fillings__cell--date';
    dateTd.textContent = filing.date;

    const formTd = document.createElement('td');
    formTd.className = 'ia-fillings__cell ia-fillings__cell--filing';
    formTd.textContent = filing.formType;

    const descTd = document.createElement('td');
    descTd.className = 'ia-fillings__cell ia-fillings__cell--description';
    descTd.textContent = filing.description;

    const actionsTd = document.createElement('td');
    actionsTd.className = 'ia-fillings__cell ia-fillings__cell--actions';

    const actionsWrap = document.createElement('div');
    actionsWrap.className = 'ia-fillings__actions';

    const link = document.createElement('a');
    link.href = filing.viewUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.className = 'ia-fillings__action-btn';
    link.setAttribute('aria-label', 'View filing');

    link.innerHTML =
      '<svg width=\"38\" height=\"38\" viewBox=\"0 0 38 38\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\">' +
      '<path d=\"M17.1 9.5H20.9V13.3H17.1V9.5ZM19 28.5C20.045 28.5 20.9 27.645 20.9 26.6V19C20.9 17.955 20.045 17.1 19 17.1C17.955 17.1 17.1 17.955 17.1 19V26.6C17.1 27.645 17.955 28.5 19 28.5ZM19 0C8.512 0 0 8.512 0 19C0 29.488 8.512 38 19 38C29.488 38 38 29.488 38 19C38 8.512 29.488 0 19 0ZM19 34.2C10.621 34.2 3.8 27.379 3.8 19C3.8 10.621 10.621 3.8 19 3.8C27.379 3.8 34.2 10.621 34.2 19C34.2 27.379 27.379 34.2 19 34.2Z\" fill=\"#1C2F45\"/>' +
      '</svg>';

    actionsWrap.appendChild(link);
    actionsTd.appendChild(actionsWrap);

    tr.appendChild(dateTd);
    tr.appendChild(formTd);
    tr.appendChild(descTd);
    tr.appendChild(actionsTd);

    return tr;
  }

  populateFilters() {
    if (!this.allFilings.length) return;

    const yearsSet = new Set();
    const formsSet = new Set();

    this.allFilings.forEach((filing) => {
      if (filing.year) yearsSet.add(filing.year);
      if (filing.formType) formsSet.add(filing.formType);
    });

    const years = Array.from(yearsSet).sort((a, b) => Number(b) - Number(a));
    const forms = Array.from(formsSet).sort();

    if (this.yearSelect) {
      years.forEach((year) => {
        const option = document.createElement('option');
        option.value = year;
        option.textContent = year;
        this.yearSelect.appendChild(option);
      });
    }

    if (this.formSelect) {
      forms.forEach((form) => {
        const option = document.createElement('option');
        option.value = form;
        option.textContent = form;
        this.formSelect.appendChild(option);
      });
    }
  }

  bindFilterEvents() {
    if (this.yearSelect) {
      this.yearSelect.addEventListener('change', () => this.applyFilters());
    }
    if (this.formSelect) {
      this.formSelect.addEventListener('change', () => this.applyFilters());
    }
  }

  applyFilters() {
    this.currentPage = 1;

    const selectedYear = this.yearSelect ? this.yearSelect.value : '';
    const selectedForm = this.formSelect ? this.formSelect.value : '';

    const filtered = this.allFilings.filter((filing) => {
      const matchYear = !selectedYear || filing.year === selectedYear;
      const matchForm = !selectedForm || filing.formType === selectedForm;
      return matchYear && matchForm;
    });

    this.filteredFilings = filtered;
    this.renderCurrentPage();
    this.updateLoadMoreVisibility();
  }

  renderCurrentPage() {
    const startIndex = 0;
    const endIndex = this.currentPage * this.itemsPerPage;
    const pageItems = this.filteredFilings.slice(startIndex, endIndex);
    this.renderRows(pageItems);
  }

  loadMore() {
    if (!this.filteredFilings.length) return;

    this.currentPage += 1;
    this.renderCurrentPage();
    this.updateLoadMoreVisibility();
  }

  updateLoadMoreVisibility() {
    if (!this.controlsWrap || !this.loadMoreBtn) return;

    if (!this.filteredFilings.length || this.filteredFilings.length <= this.currentPage * this.itemsPerPage) {
      this.controlsWrap.style.display = 'none';
    } else {
      this.controlsWrap.style.display = 'flex';
    }
  }
}

customElements.define('ia-fillings', IAFillings);
